import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApolloError, useApolloClient } from '@apollo/client';

import {
  ExpensesDocument,
  ExpensesQuery,
  ExpensesQueryVariables,
} from 'core/graphql';

import { IExpenseListRow, IExpensesFilters } from './types';
import { isPermissionDeniedError } from './errors';

interface IAllExpensesHook {
  data: IExpenseListRow[];
  loading: boolean;
  fetching: boolean;
  error?: ApolloError;
  permissionDenied: boolean;
  refetch: () => void;
}

const BATCH_SIZE = 200;

/**
 * Sibling of `useAllFakturowniaInvoices` for the Expense list. Walks every
 * page of `expenses` until the backend returns a short page; consumers get
 * a flat array of every row that matches the filter. Phase 9.1: replaces
 * the page-capped `useExpenses` in views that wanted «всё в выборке»
 * semantics — Expense register, GroupDetail Finances, TeacherDetail Payouts.
 */
export const useAllExpenses = (filters: IExpensesFilters): IAllExpensesHook => {
  const client = useApolloClient();
  const [rows, setRows] = useState<IExpenseListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApolloError | undefined>(undefined);
  const [refetchTick, setRefetchTick] = useState(0);

  const baseVariables: Omit<
    ExpensesQueryVariables,
    'page' | 'perPage'
  > = useMemo(
    () => ({
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
      source: filters.source || undefined,
      categorySlug: filters.categorySlug || undefined,
      teacherId: filters.teacherId || undefined,
      groupId: filters.groupId || undefined,
      legalEntityId: filters.legalEntityId || undefined,
    }),
    [
      filters.dateFrom,
      filters.dateTo,
      filters.source,
      filters.categorySlug,
      filters.teacherId,
      filters.groupId,
      filters.legalEntityId,
    ],
  );

  const variablesKey = JSON.stringify(baseVariables);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    // Phase 11: stale-while-revalidate — DON'T blank `rows` here. The page
    // dims the previous list while the next set streams in; wiping early
    // would trigger the full-screen preloader on every filter change.

    const fetchAll = async () => {
      const accumulator: IExpenseListRow[] = [];
      let page = 1;
      try {
        // Sequential by design: we only know if a next page exists after
        // the current one returns. `no-await-in-loop` doesn't apply.
        // eslint-disable-next-line no-constant-condition, no-await-in-loop
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const result = await client.query<
            ExpensesQuery,
            ExpensesQueryVariables
          >({
            query: ExpensesDocument,
            variables: { ...baseVariables, page, perPage: BATCH_SIZE },
            fetchPolicy: 'network-only',
          });
          if (cancelled) return;

          const batch = (
            result.data?.expenses || []
          ).filter((r): r is IExpenseListRow => Boolean(r));
          accumulator.push(...batch);
          setRows(accumulator.slice());

          if (batch.length < BATCH_SIZE) break;
          page += 1;
        }
      } catch (e) {
        if (!cancelled) setError(e as ApolloError);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variablesKey, refetchTick, client]);

  const refetch = useCallback(() => setRefetchTick((t) => t + 1), []);

  return {
    data: rows,
    loading,
    fetching: loading,
    error,
    permissionDenied: isPermissionDeniedError(error),
    refetch,
  };
};
